import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface ProgressBarProps extends Omit<BoxProps, 'children'> {
  /** 0-100 */
  value: number
  /** Track height in px. Defaults to 4. */
  thickness?: number
}

const Track = styled(Box, {
  shouldForwardProp: (p) => p !== 'thickness',
})<{ thickness: number }>(({ thickness }) => ({
  height: thickness,
  borderRadius: thickness,
  background: 'var(--ds-hover)',
  overflow: 'hidden',
}))

const Fill = styled(Box, {
  shouldForwardProp: (p) => p !== 'value',
})<{ value: number }>(({ value }) => ({
  height: '100%',
  width: `${Math.min(100, Math.max(0, value))}%`,
  borderRadius: 'inherit',
  background: 'var(--ds-gradient-spectrum, var(--ds-accent))',
  transition: 'width var(--ds-duration-slow, 300ms) var(--ds-ease)',
}))

/** Thin linear determinate progress bar with the signature gradient fill. */
export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(
  { value, thickness = 4, ...rest },
  ref,
) {
  return (
    <Track
      ref={ref}
      thickness={thickness}
      role='progressbar'
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
      {...rest}
    >
      <Fill value={value} />
    </Track>
  )
})
